// SPDX-License-Identifier: AGPL-3.0-or-later

import type {RoomBodyEmote, RoomCutaway, RoomEmote, Vec3} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import {
	decodeHead,
	decodePos,
	ROOM_EMOTE_TOPIC,
	ROOM_HEAD_TOPIC,
	ROOM_LIMITS,
	ROOM_POS_TOPIC,
	type RoomPose,
} from '@fluxer/constants/src/RoomCharacters';
import {
	decodeRoomEmotes,
	isRoomBodyEmoteKey,
	ROOM_BODY_EMOTES,
	ROOM_EMOTE_CODES,
} from '@fluxer/constants/src/RoomEmotes';

// What a room data packet may carry. The sender is never in it: only LiveKit's participant names the sender.
export type RoomSignal =
	| {emote: RoomEmote}
	| {head: {yawDeg: number; pitchDeg: number}}
	| {pos: RoomPose & {end: boolean}};

// The emotes this room accepts: Fluxer's unicode emoji data, this guild's emojis and the pets of this layout.
export interface RoomSignalScope {
	isUnicodeEmoji(emoji: string): boolean;
	isRoomEmoji(id: string): boolean;
	isPet(id: string): boolean;
}

// The participant attribute the API writes while a guild server-mutes someone ('true'); their emotes are not shown.
export const SERVER_MUTE_ATTRIBUTE = 'server_mute';

type EmoteWire =
	| {k: 'e'; v: string}
	| {k: 'c'; v: string}
	| {k: 'w' | 'd' | 'j' | 's' | 'u' | 'f'}
	| {k: 'p'; v: string}
	| {k: 'b'; y: number; p: number}
	| {k: 't'; v: string}
	| {k: 'a'; v: string; o?: 1};

// A point's direction on the wire, whole degrees.
const POINT_YAW_MAX_DEG = 180;
const POINT_PITCH_MAX_DEG = 30;
// A slap seen is answered by its target within this long, or not at all.
const SLAP_ANSWER_MS = 1500;
// A slap between the same two people at most this often; at the same target, from anyone, this often. One a second
// from a slapper (its emote limit) lands each time, so two or three in a row can knock out (RoomKnockout).
const SLAP_PAIR_MS = 800;
const SLAP_TARGET_MS = 600;
// An onlooker allows this share of those times: network jitter bunches answers the target spaced correctly.
export const SLAP_ONLOOKER_SHARE = 0.8;

const BODY_BY_CODE = new Map<string, RoomBodyEmote>(ROOM_BODY_EMOTES.map((kind) => [ROOM_EMOTE_CODES[kind], kind]));

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder('utf-8', {fatal: true});
const SNOWFLAKE = /^\d{1,20}$/;
const HEAD_INTERVAL_MS = 1000 / ROOM_LIMITS.headReceiveMaxHz;
const POS_INTERVAL_MS = 1000 / ROOM_LIMITS.posReceiveMaxHz;
const POS_END_INTERVAL_MS = ROOM_LIMITS.posIdleHeartbeatS * 1000;
const POS_JUMPS_TO_IGNORE = 3;
const POS_IGNORE_MS = 10_000;
// The time between two packets counts this much at most: waiting is no way round the speed limit.
const POS_GAP_MAX_MS = 1000;
// A stall that bunched packets up: a run that agrees with the packet dropped just before it takes over, as far as this
// much more time would allow from the last good packet.
const POS_STALL_MS = 500;
// After sitting back down, a new walk starts this near (floor plan) the sender's own place.
const POS_NEW_WALK_REACH_M = 2;
// GLB contract: the floor is at y = 0.
const ROOM_FLOOR_Y = 0;

const wholeDegrees = (value: number, max: number): number => Math.max(-max, Math.min(max, Math.round(value) || 0));

function emoteWire(emote: RoomEmote): EmoteWire {
	switch (emote.kind) {
		case 'emoji':
			return {k: 'e', v: emote.emoji};
		case 'custom':
			return {k: 'c', v: emote.id};
		case 'pet':
			return {k: 'p', v: emote.pet};
		case 'wave':
			return {k: 'w'};
		case 'point':
			return {
				k: 'b',
				y: wholeDegrees(emote.yawDeg, POINT_YAW_MAX_DEG),
				p: wholeDegrees(emote.pitchDeg, POINT_PITCH_MAX_DEG),
			};
		case 'slap':
			return {k: 't', v: emote.target};
		case 'slapped':
			return emote.knockout ? {k: 'a', v: emote.by, o: 1} : {k: 'a', v: emote.by};
		default:
			return {k: ROOM_EMOTE_CODES[emote.kind]};
	}
}

export function encodeEmote(emote: RoomEmote): Uint8Array<ArrayBuffer> {
	return TEXT_ENCODER.encode(JSON.stringify(emoteWire(emote)));
}

const inDegrees = (value: unknown, max: number): value is number =>
	Number.isInteger(value) && Math.abs(value as number) <= max;

function readEmote(payload: Uint8Array): RoomEmote | null {
	if (payload.byteLength === 0 || payload.byteLength > ROOM_LIMITS.emotePayloadMaxBytes) return null;
	let wire: unknown;
	try {
		wire = JSON.parse(TEXT_DECODER.decode(payload));
	} catch {
		return null;
	}
	if (typeof wire !== 'object' || wire === null) return null;
	const {k, v, y, p, o} = wire as {k?: unknown; v?: unknown; y?: unknown; p?: unknown; o?: unknown};
	if (k === 'w') return {kind: 'wave'};
	const body = typeof k === 'string' ? BODY_BY_CODE.get(k) : undefined;
	if (body) return {kind: body};
	if (k === 'b')
		return inDegrees(y, POINT_YAW_MAX_DEG) && inDegrees(p, POINT_PITCH_MAX_DEG)
			? {kind: 'point', yawDeg: y, pitchDeg: p}
			: null;
	if (typeof v !== 'string') return null;
	if (k === 'e') return {kind: 'emoji', emoji: v};
	if (k === 'c') return {kind: 'custom', id: v};
	if (k === 'p') return {kind: 'pet', pet: v};
	if (k === 't' && SNOWFLAKE.test(v)) return {kind: 'slap', target: v};
	if (k === 'a' && SNOWFLAKE.test(v))
		return o === 1 ? {kind: 'slapped', by: v, knockout: true} : {kind: 'slapped', by: v};
	return null;
}

function isAllowed(emote: RoomEmote, scope: RoomSignalScope): boolean {
	switch (emote.kind) {
		case 'emoji':
			return scope.isUnicodeEmoji(emote.emoji);
		case 'custom':
			return SNOWFLAKE.test(emote.id) && scope.isRoomEmoji(emote.id);
		case 'pet':
			return scope.isPet(emote.pet);
		case 'wave':
		case 'dance':
		case 'jump':
		case 'crouch':
		case 'punch':
		case 'faint':
		case 'point':
		case 'slap':
		case 'slapped':
			return true;
	}
}

// Pointing and slapping: aimed at a direction or a person, they are neither sent nor taken in a room of more people
// than characters drawn.
export function isAimedEmote(emote: RoomEmote): boolean {
	return emote.kind === 'point' || emote.kind === 'slap' || emote.kind === 'slapped';
}

export function aimedEmotesOff(room: {remoteParticipants: ReadonlyMap<string, unknown>}): boolean {
	return room.remoteParticipants.size + 1 > ROOM_LIMITS.charactersMax;
}

// The wave, emoji and pet emotes are everyone's; a body emote only plays when the micup.emotes attribute of the
// person sending it lists it. A missing or unreadable attribute leaves only the wave.
export function hasRoomEmote(emote: RoomEmote, attribute: string | undefined): boolean {
	return !isRoomBodyEmoteKey(emote.kind) || (decodeRoomEmotes(attribute)?.has(emote.kind) ?? false);
}

// The topic is checked before anything is decoded; anything unknown, oversized or outside the scope is null.
export function parseRoomSignal(
	topic: string | undefined,
	payload: Uint8Array,
	scope: RoomSignalScope,
): RoomSignal | null {
	if (topic === ROOM_HEAD_TOPIC) {
		const head = decodeHead(payload);
		return head ? {head} : null;
	}
	if (topic === ROOM_POS_TOPIC) {
		const pos = decodePos(payload);
		return pos ? {pos} : null;
	}
	if (topic !== ROOM_EMOTE_TOPIC) return null;
	const emote = readEmote(payload);
	return emote && isAllowed(emote, scope) ? {emote} : null;
}

// Inside the room's four wall half-planes (at least `inset` from each) and between the floor and the ceiling. Walls are
// projected onto in turn; a box needs one pass, the extra passes settle an odd-shaped room.
export function clampToRoom<T extends Vec3>(point: T, cutaway: RoomCutaway, inset = 0): T {
	let {x, z} = point;
	for (let pass = 0; pass < 4; pass++) {
		let inside = true;
		for (const wall of cutaway.walls) {
			const out = (x - wall.point.x) * wall.normal.x + (z - wall.point.z) * wall.normal.z + inset;
			if (out <= 0) continue;
			x -= out * wall.normal.x;
			z -= out * wall.normal.z;
			inside = false;
		}
		if (inside) break;
	}
	return {...point, x, z, y: Math.min(cutaway.ceiling.y, Math.max(ROOM_FLOOR_Y, point.y))};
}

export interface EmoteLimit {
	intervalMs: number;
	windowMs: number;
}

// A sender holds its own emotes to 1 a second and 5 in 10 seconds.
export const EMOTE_SEND_LIMIT: EmoteLimit = {intervalMs: 1000 / ROOM_LIMITS.emotePerSecond, windowMs: 10_000};
// A receiver allows 0.8 s spacing and 5 in 9 seconds: network jitter bunches emotes the sender spaced correctly, and
// those must not be dropped. A flood is still cut to the same order.
const EMOTE_RECEIVE_LIMIT: EmoteLimit = {intervalMs: EMOTE_SEND_LIMIT.intervalMs * 0.8, windowMs: 9000};

interface PosState {
	last: {at: number; point: Vec3} | null;
	// The packet dropped just before, as a jump.
	candidate: {at: number; point: Vec3} | null;
	jumps: number;
	ignoredUntil: number;
	endAt: number;
	// Back in its place since the last walk: the next one starts near it.
	seated: boolean;
}

// Whether `point` is within the speed limit of `from` at `now`, the time counting POS_GAP_MAX_MS at most, plus `slackMs`.
function reachable(from: {at: number; point: Vec3}, point: Vec3, now: number, slackMs = 0): boolean {
	const metres = Math.hypot(point.x - from.point.x, point.y - from.point.y, point.z - from.point.z);
	return metres <= (ROOM_LIMITS.posMaxSpeedMps * (Math.min(now - from.at, POS_GAP_MAX_MS) + slackMs)) / 1000;
}

// Per sender: emotes within the given limit (a receiver's by default), head packets at most 4 a second, positions at
// most 12 a second and no faster than 6 m/s from the last good one (the gap counting a second at most; a run agreeing
// with the packet dropped just before takes over after a stall; three jumps in a row ignore the sender for 10 s; back in
// its place, the next walk starts within 2 m of it); the rest is dropped. Keeps at most 5 times per sender; a sender who
// leaves is forgotten. Slaps (by user id): the ones seen, until answered or 1.5 s old, and the ones that landed, at most
// once in 0.8 s between the same two people and once in 0.6 s at the same target (a share of those for an onlooker);
// entries past those times are dropped.
export class RoomSignalLimiter {
	private readonly emotes = new Map<string, Array<number>>();
	private readonly heads = new Map<string, number>();
	private readonly positions = new Map<string, PosState>();
	private readonly slapsSeen = new Map<string, number>();
	private readonly slapPairs = new Map<string, number>();
	private readonly slapTargets = new Map<string, number>();

	constructor(private readonly emoteLimit: EmoteLimit = EMOTE_RECEIVE_LIMIT) {}

	noteSlap(slapper: string, target: string, now: number): void {
		dropOlder(this.slapsSeen, now - SLAP_ANSWER_MS);
		this.slapsSeen.set(`${slapper} ${target}`, now);
	}

	// When the slap `target` answers was seen, once; null when there was none within 1.5 s.
	takeSlap(slapper: string, target: string, now: number): number | null {
		const key = `${slapper} ${target}`;
		const at = this.slapsSeen.get(key);
		this.slapsSeen.delete(key);
		return at !== undefined && now - at <= SLAP_ANSWER_MS ? at : null;
	}

	// Whether a slap may land now; recordSlap once it has.
	slapAllowed(slapper: string, target: string, now: number, share = 1): boolean {
		return (
			now - (this.slapPairs.get(`${slapper} ${target}`) ?? Number.NEGATIVE_INFINITY) >= SLAP_PAIR_MS * share &&
			now - (this.slapTargets.get(target) ?? Number.NEGATIVE_INFINITY) >= SLAP_TARGET_MS * share
		);
	}

	recordSlap(slapper: string, target: string, now: number): void {
		dropOlder(this.slapPairs, now - SLAP_PAIR_MS);
		dropOlder(this.slapTargets, now - SLAP_TARGET_MS);
		this.slapPairs.set(`${slapper} ${target}`, now);
		this.slapTargets.set(target, now);
	}

	private recentEmotes(sender: string, now: number): Array<number> {
		return (this.emotes.get(sender) ?? []).filter((at) => now - at < this.emoteLimit.windowMs);
	}

	nextEmoteAt(sender: string, now: number): number {
		const recent = this.recentEmotes(sender, now);
		const last = recent[recent.length - 1];
		if (last === undefined) return now;
		const spaced = last + this.emoteLimit.intervalMs;
		const windowed = recent.length >= ROOM_LIMITS.emotePerTenSeconds ? recent[0]! + this.emoteLimit.windowMs : now;
		return Math.max(now, spaced, windowed);
	}

	allowEmote(sender: string, now: number): boolean {
		if (this.nextEmoteAt(sender, now) > now) return false;
		this.emotes.set(sender, [...this.recentEmotes(sender, now), now]);
		return true;
	}

	allowHead(sender: string, now: number): boolean {
		const last = this.heads.get(sender);
		if (last !== undefined && now - last < HEAD_INTERVAL_MS) return false;
		this.heads.set(sender, now);
		return true;
	}

	private posState(sender: string): PosState {
		let state = this.positions.get(sender);
		if (!state) {
			state = {
				last: null,
				candidate: null,
				jumps: 0,
				ignoredUntil: Number.NEGATIVE_INFINITY,
				endAt: Number.NEGATIVE_INFINITY,
				seated: false,
			};
			this.positions.set(sender, state);
		}
		return state;
	}

	// home: the sender's seat or stand point, where its next walk after sitting back down must start (unknown: anywhere).
	allowPos(sender: string, point: Vec3, now: number, home: Vec3 | null = null): boolean {
		const state = this.posState(sender);
		if (now < state.ignoredUntil) return false;
		const {last, candidate} = state;
		if (last && now - last.at < POS_INTERVAL_MS) return false;
		if (!last) {
			const away = home && Math.hypot(point.x - home.x, point.z - home.z) > POS_NEW_WALK_REACH_M;
			if (state.seated && away) return this.jump(state, null, now);
		} else if (
			!reachable(last, point, now) &&
			!(candidate && reachable(candidate, point, now) && reachable(last, point, now, POS_STALL_MS))
		)
			return this.jump(state, point, now);
		state.last = {at: now, point: {x: point.x, y: point.y, z: point.z}};
		state.candidate = null;
		state.jumps = 0;
		state.seated = false;
		return true;
	}

	// A dropped jump; `point` (when given) may start the run a stall bunched up. Three in a row ignore the sender.
	private jump(state: PosState, point: Vec3 | null, now: number): false {
		state.candidate = point ? {at: now, point: {x: point.x, y: point.y, z: point.z}} : null;
		if (++state.jumps >= POS_JUMPS_TO_IGNORE) {
			state.ignoredUntil = now + POS_IGNORE_MS;
			state.jumps = 0;
			state.last = null;
			state.candidate = null;
			state.seated = false;
		}
		return false;
	}

	// A walker back in its seat; the next walk may start from anywhere (its seat). At most one per heartbeat, so ending
	// is no way round the jump check.
	allowPosEnd(sender: string, now: number): boolean {
		const state = this.posState(sender);
		if (now < state.ignoredUntil || now - state.endAt < POS_END_INTERVAL_MS) return false;
		state.endAt = now;
		state.last = null;
		state.candidate = null;
		state.jumps = 0;
		state.seated = true;
		return true;
	}

	forget(sender: string): void {
		this.emotes.delete(sender);
		this.heads.delete(sender);
		this.positions.delete(sender);
	}

	clear(): void {
		this.emotes.clear();
		this.heads.clear();
		this.positions.clear();
		this.slapsSeen.clear();
		this.slapPairs.clear();
		this.slapTargets.clear();
	}
}

function dropOlder(times: Map<string, number>, before: number): void {
	for (const [key, at] of times) if (at < before) times.delete(key);
}
